<template>
  <OrchestratorContainer>

    <ProgressBar v-if="isWizardMode" />

    <BodyCard>

      <template v-if="isWizardMode && engine.currentSection.value">
        <h2 :class="['pe-section-title', props.config?.classes?.sectionTitle]" v-if="engine.currentSection.value.title">
          {{ engine.currentSection.value.title }}
        </h2>

        <FormQuestion v-for="q in engine.currentSection.value.questions" :key="q.id" :question="q"
          :error="showErrors ? q.error : null" />
      </template>

      <template v-else>
        <div v-for="section in engine.sections.value" :key="section.id" class="pe-section-block">
          <h2 :class="['section-title', props.config?.classes?.sectionTitle]" v-if="section.title">{{ section.title }}
          </h2>

          <FormQuestion v-for="q in section.questions" :key="q.id" :question="q" :error="showErrors ? q.error : null" />
          <hr v-if="section.title" class="section-divider" />
        </div>
      </template>

    </BodyCard>

    <div v-if="engine.globalError.value" :class="['pe-error-msg', props.config?.classes?.globalError]">
      {{ engine.globalError.value }}
    </div>

    <div class="pe-finalization-btn-container" v-if="engine.isLastStep.value || !isWizardMode">
      <PeButton
        :loading="engine.loading.value"
        loading-text="Enviando..."
        :class="[props.config?.classes?.submitBtn]"
        :disabled="engine.loading.value"
        @click="handleSubmit"
      >
        {{ submitBtnText }}
      </PeButton>
    </div>

    <div class="pe-actions-footer" v-if="isWizardMode">
      <div class="pe-btn-prev-container">
        <button class="pe-btn pe-btn-prev" :class="[config?.classes?.prevBtn]" @click="handlePrev">
          <IconArrowCircle direction="left" class="pe-btn-icon" />
          <span>Volver</span>
        </button>
      </div>
      <div class="pe-btn-next-container">
        <button v-if="!engine.isLastStep.value" class="pe-btn pe-btn-next" :class="[config?.classes?.nextBtn]"
          @click="handleNext">
          <span>Siguiente</span>
          <IconArrowCircle direction="right" class="pe-btn-icon" />
        </button>
      </div>
    </div>

  </OrchestratorContainer>
</template>

<script setup>
import { useFormEngine } from '../../composables/useFormEngine';
import BodyCard from '../Shared/BodyCard.vue';
import IconArrowCircle from '../Shared/IconArrowCircle.vue';
import OrchestratorContainer from '../Shared/OrchestratorContainer.vue';
import PeButton from '../Shared/PeButton.vue';
import ProgressBar from '../Shared/ProgressBar.vue';
import FormQuestion from './FormQuestion.vue';
import { computed, nextTick, onMounted, provide, ref, watch } from 'vue';
const props = defineProps({
  schema: { type: Array, required: true },
  adapter: { type: Object, required: true },
  extraData: { type: Object, default: () => ({}) },
  extraPayload: { type: Object, default: () => ({}) },
  procedureId: { type: Number, required: true },
  displayMode: {
    type: String,
    default: 'wizard',
    validator: (value) => ['wizard', 'list'].includes(value)
  },
  config: {
    type: Object,
    default: () => ({
      mapCenter: { lat: -31.6476596, lng: -63.3449318 },
      classes: {
        title: '',       // Para el título de la pregunta
        label: '',       // Para las etiquetas
        input: '',       // Para los inputs de texto
        buttonNext: '',  // Para botón siguiente
        buttonPrev: '',  // Para botón atrás
        error: '',        // Para mensajes de error,
        placeholder: '',    // Para placeholders
        sectionTitle: '',   // Para el título de la sección
      }
    })
  }
});

const emit = defineEmits(['success', 'error', 'cancel']);

// INICIALIZAMOS MOTOR
const engine = useFormEngine(
  props.schema,
  props.adapter,
  {
    autoFillFromUrl: true,
    enableAutoSave: true,
    procedureId: props.procedureId,
    basePrice: props.extraData?.price,
  }
);

provide('formEngine', engine);
provide('engineConfig', computed(() => props.config));
const showErrors = ref(false);

const isWizardMode = computed(() => props.displayMode === 'wizard');

const focusAndScrollToTop = async () => {
  await nextTick(); // Esperamos a que Vue renderice el nuevo paso
  const target = document.querySelector('.pe-question-wrapper .pe-input');
  
  if (target) {
    await nextTick();
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  setTimeout(async () => {
    const firstInput = document.querySelector('.pe-question-wrapper .pe-input')
    await nextTick();
    if (firstInput) {
      firstInput.focus({ preventScroll: true }); 
    }
  }, 150); // Pequeño delay extra para asegurar que las animaciones/render terminen
};

// HANDLERS DE NAVEGACIÓN (Igual que antes)
const handleNext = async () => {
  showErrors.value = true;
  if (engine.isCurrentSectionValid.value) {
    engine.nextStep();
    showErrors.value = false;
    // window.scrollTo(0, 0);
    await focusAndScrollToTop();
  }
};

const handlePrev = async () => {
  if (engine.isFirstStep.value) {
    emit('cancel');
    return;
  }
  engine.prevStep();
  // window.scrollTo(0, 0);
  await focusAndScrollToTop();
};

const handleSubmit = async () => {
  showErrors.value = true;
  if (!engine.isFormValid.value) {
    if (!isWizardMode.value) scrollToFirstError();
    return;
  }

  try {
    await engine.submit(props.extraPayload);
    emit('success');
  } catch (e) {
    emit('error', e.message);
  }
};

const submitBtnText = computed(() => {
  const customTexts = props.config?.texts?.submitBtn;

  if (engine.totalPrice.value > 0) {
    return customTexts?.payment ?? 'Finalizar y Pagar';
  } 
  return customTexts?.default ?? 'Finalizar';
});

// ==============================================================
// LÓGICA DE PRECARGA AUTOMÁTICA
// ==============================================================
const syncContactNumber = () => {
  const externalPhone = props.extraPayload?.contactNumber;
  if (!externalPhone) return;
  const contactQ = engine.questions.find(q =>
    q.options.some(opt => opt.type === 'contact_number')
  );

  if (contactQ) {
    const option = contactQ.options.find(opt => opt.type === 'contact_number');
    if (option && !option.value) {
      engine.setAnswer(contactQ.id, option.id, externalPhone);
    }
  }
};

watch(
  () => props.extraPayload,
  (oldVal, newVal) => {
    if (oldVal?.contactNumber !== newVal?.contactNumber) syncContactNumber();
  }
);

onMounted(async () => {
  if (props.extraPayload?.contactNumber) syncContactNumber();
  await focusAndScrollToTop();
});

const scrollToFirstError = () => {
  setTimeout(() => {
    const firstError = document.querySelector('.pe-error-msg, .pe-input-error');
    if (firstError) {
      firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, 100);
}

defineExpose({
  engine,          
  handleNext,      
  handlePrev,
  handleSubmit
});
</script>

<style>
.pe-orchestrator-container * {
  box-sizing: border-box;
}

.pe-input,
.pe-textarea {
  display: block;
  width: 100%;

  border: var(--pe-input-border, 1px solid var(--pe-input-border-color, #F5F5F5));
  padding: var(--pe-input-padding, 0.75vw);
  background: var(--pe-input-bg, #F5F5F5);

  border-radius: var(--pe-input-border-radius, 0.5rem) !important;

  font-family: var(--pe-input-font-family, inherit);
  font-weight: var(--pe-input-font-weight, inherit);
  line-height: var(--pe-input-line-height, inherit);
  color: var(--pe-input-text-color, #333);

  transition: border-color 0.2s;
}

.pe-input:focus,
.pe-textarea:focus {
  outline: none;
}

.pe-textarea {
  min-height: var(--pe-textarea-min-height, 14vh);
  resize: none;
  overflow-y: hidden;
}

.pe-error-msg {
  color: var(--pe-error-color, #d32f2f);
  margin: var(--pe-error-mg, 0px);
  display: var(--pe-error-display, block);
}

.pe-option-container {
  display: flex;
  flex-direction: column;
  gap: var(--pe-options-gp, 1.5vh);
}
</style>

<style scoped>
.pe-section-block {
  display: flex;
  flex-direction: column;
  gap: var(--pe-form-body-gp, 4.1vh);
}

.pe-section-title {
  color: var(--pe-section-title-color, #333);
  border-bottom: var(--pe-section-title-border-btm, none);
  font-style: inherit;
}

.pe-actions-footer {
  display: flex;
  align-items: center;
  gap: var(--pe-actions-footer-gp, 1rem);
  margin-top: var(--pe-actions-footer-mg-top, 0);
  justify-content: center;
  flex-grow: 1;
}

.pe-btn {
  display: flex;
  align-items: center;
  gap: var(--pe-btn-gap, 0.5vw);

  border: var(--pe-btn-border, none);
  border-radius: var(--pe-btn-b-rd, 0);
  background: var(--pe-btn-bg, transparent);
  background-image: var(--pe-btn-bg-img, none);
  color: var(--pe-btn-color, #333);
  padding: var(--pe-btn-pd, 0);
}

.pe-btn-next-container {
  display: flex;
  flex-grow: 1;
  justify-content: flex-end;
}

.pe-btn-prev-container {
  display: flex;
  flex-grow: 1;
  justify-content: flex-start;
}

.pe-finalization-btn-container {
  display: flex;
  justify-content: center;
  margin: var(--pe-finalization-btn-container-mg, none);
}

.pe-finalization-btn {
  border: var(--pe-finalization-btn-border, none);
  border-radius: var(--pe-finalization-btn-b-rd, 0.5rem);
  background: var(--pe-btn-finalization-bg, black);
  background-image: var(--pe-btn-finalization-bg-img, none);
  color: var(--pe-btn-finalization-color, white);
  padding: var(--pe-btn-finalization-pd, 1vw 2vw);
}

.pe-finalization-btn:disabled {
  background: var(--pe-btn-finalization-disabled-bg, grey);
  background-image: var(--pe-btn-finalization-disabled-bg-img, none);
}
</style>